/**
 * CHẤM CÔNG.
 *
 * Reached from Trang chủ, from Cá nhân, and from the QR scanner when a
 * chấm-công code is scanned.
 *
 * THE THREE THINGS THIS SCREEN IS HONEST ABOUT
 * --------------------------------------------
 * 1. SERVER TIME DECIDES. The device clock is captured and sent as
 *    `deviceTime`, but only as evidence — the API turns it into
 *    `clock_skew_ms` and stamps the event with its own clock. The screen says
 *    this in plain Vietnamese rather than showing a device time that will not
 *    match the payslip.
 * 2. LOCATION IS OPTIONAL AND NEVER BLOCKS. A refusal or a failed fix still
 *    lets the employee clock in; the event is simply flagged for review. The
 *    alternative — refusing to record a shift because a basement has no GPS —
 *    is worse for everyone.
 * 3. A FLAG IS NOT A REJECTION. When the server answers PENDING_REVIEW the
 *    screen lists exactly which signals fired and what to do about each one.
 *
 * "RỜI VỊ TRÍ CÓ PHÉP" is NOT an attendance action: `POST /staff/attendance`
 * accepts only CHECK_IN / CHECK_OUT / BREAK_START / BREAK_END. The server
 * models leaving your post as a presence change, so that button posts to
 * `/staff/status` with AWAY_WITH_PERMISSION. It lives here because that is
 * where an employee looks for it.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ApiError } from '@sonauto/api-client';
import { errorMessage, queryKeys, useHome } from '../lib/queries';
import { useGeolocation, useServerClock, useSyncState } from '../lib/hooks';
import { enqueueAttendance } from '../lib/outbox';
import {
  ATTENDANCE_ACTION_LABEL,
  RISK_FLAG_ADVICE,
  RISK_FLAG_LABEL,
  dayStatusLabel,
  verificationLabel,
  verificationTone,
} from '../lib/labels';
import type {
  AttendanceActionType,
  AttendanceRequest,
  AttendanceResponse,
  AttendanceRiskFlag,
} from '../lib/contracts';
import { formatClock, formatDistance, formatMinutes, formatSkew, formatTime } from '../lib/format';
import { Banner, Button, Card, KeyValue, LoadingState, StatusBadge } from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';
import { useToast } from '../components/Toast';

interface LocationState {
  readonly qrPayload?: string;
}

type Outcome =
  | { readonly kind: 'SENT'; readonly response: AttendanceResponse }
  | { readonly kind: 'QUEUED'; readonly action: AttendanceActionType; readonly at: number };

export function AttendanceScreen(): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { online } = useSyncState();
  const home = useHome();
  const now = useServerClock(home.data?.serverTime, 1000);
  const geo = useGeolocation();

  const qrPayload = (location.state as LocationState | null)?.qrPayload ?? null;
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [pendingAction, setPendingAction] = useState<AttendanceActionType | null>(null);

  // Ask for a position as soon as the screen opens so the fix is warm by the
  // time a thumb reaches the button — but never block on it.
  useEffect(() => {
    void geo.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount
  }, []);

  const submit = useMutation({
    mutationFn: async (actionType: AttendanceActionType): Promise<Outcome> => {
      // One fresh fix per tap. A stale position from ten minutes ago would be
      // evidence of the wrong place.
      const fix = await geo.request();

      const request: AttendanceRequest = {
        actionType,
        // Client UUID = the server's idempotency key. The same physical tap,
        // however many times it is replayed, is one attendance event.
        clientEventId: crypto.randomUUID(),
        deviceTime: Date.now(),
        latitude: fix?.latitude ?? null,
        longitude: fix?.longitude ?? null,
        accuracyM: fix?.accuracyM ?? null,
        verificationMethod: qrPayload !== null ? 'QR_BRANCH' : 'GPS',
        qrPayload,
        networkInfo: describeNetwork(),
      };

      if (!online) {
        await enqueueAttendance(request, `Chấm công: ${ATTENDANCE_ACTION_LABEL[actionType]}`);
        return { kind: 'QUEUED', action: actionType, at: Date.now() };
      }

      try {
        return { kind: 'SENT', response: await api.attendance(request) };
      } catch (error) {
        // Lost the connection mid-request: queue it rather than lose the tap.
        if (error instanceof ApiError && error.isConnectivityError) {
          await enqueueAttendance(request, `Chấm công: ${ATTENDANCE_ACTION_LABEL[actionType]}`);
          return { kind: 'QUEUED', action: actionType, at: Date.now() };
        }
        throw error;
      }
    },
    onSuccess: (result) => {
      setOutcome(result);
      setPendingAction(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.home });
      if (result.kind === 'QUEUED') {
        toast.warning('Đã lưu, chờ gửi khi có mạng', 'Giờ ghi nhận sẽ là lúc máy chủ nhận được.');
      } else if (result.response.verificationResult === 'PENDING_REVIEW') {
        toast.warning('Đã ghi nhận, cần quản lý xác minh', 'Xem chi tiết bên dưới.');
      } else {
        toast.success(`Đã ${ATTENDANCE_ACTION_LABEL[result.response.actionType].toLowerCase()}`);
      }
    },
    onError: (error) => {
      setPendingAction(null);
      toast.error('Chấm công không thành công', errorMessage(error));
    },
  });

  const away = useMutation({
    mutationFn: () => api.setStatus({ status: 'AWAY_WITH_PERMISSION', reason: 'Rời vị trí có phép' }),
    onSuccess: () => {
      toast.success('Đã báo rời vị trí có phép');
      void queryClient.invalidateQueries({ queryKey: queryKeys.home });
    },
    onError: (error) => toast.error('Không cập nhật được trạng thái', errorMessage(error)),
  });

  if (home.isLoading) return <LoadingState />;

  const attendance = home.data?.attendance;
  const checkedIn = attendance?.checkedIn ?? false;
  const onBreak = attendance?.lastAction === 'BREAK_START';
  const workedSeconds =
    (attendance?.workedMinutes ?? 0) * 60 +
    (checkedIn && home.data !== undefined ? Math.max(0, Math.floor((now - home.data.serverTime) / 1000)) : 0);

  const run = (action: AttendanceActionType): void => {
    setPendingAction(action);
    setOutcome(null);
    submit.mutate(action);
  };

  return (
    <div className="pb-8">
      <ScreenHeader title="Chấm công" subtitle={home.data?.businessDate} />

      {qrPayload === null ? null : (
        <Banner tone="info" title="Đã quét mã QR chấm công">
          Mã QR sẽ được gửi kèm để xác minh bạn có mặt tại cửa hàng.
        </Banner>
      )}

      {/* --- current state ------------------------------------------------ */}
      <Card className="mt-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm text-slate-500">Đã làm hôm nay</p>
            <p className="text-4xl font-black tabular-nums text-slate-900">{formatClock(workedSeconds)}</p>
          </div>
          <StatusBadge tone={checkedIn ? 'success' : 'neutral'}>
            {onBreak ? 'Đang nghỉ giữa ca' : checkedIn ? 'Đang trong ca' : 'Ngoài ca'}
          </StatusBadge>
        </div>
        <div className="mt-3 divide-y divide-slate-100 border-t border-slate-100">
          <KeyValue label="Vào ca lúc" value={formatTime(attendance?.firstInAt)} />
          <KeyValue label="Tan ca lúc" value={formatTime(attendance?.lastOutAt)} />
          <KeyValue label="Nghỉ giữa ca" value={formatMinutes(attendance?.breakMinutes ?? 0)} />
          <KeyValue label="Đi muộn" value={formatMinutes(attendance?.lateMinutes ?? 0)} />
          <KeyValue label="Trạng thái công" value={dayStatusLabel(attendance?.dayStatus ?? 'INCOMPLETE')} />
        </div>
      </Card>

      {/* --- location ------------------------------------------------------ */}
      <LocationCard geo={geo.state} onRetry={() => void geo.request()} />

      {/* --- server-time notice -------------------------------------------- */}
      <p className="mt-3 rounded-2xl bg-slate-100 p-3 text-xs leading-relaxed text-slate-600">
        Giờ chấm công được ghi theo <strong>giờ máy chủ</strong>, không phải giờ trên điện thoại. Giờ thiết bị
        vẫn được gửi kèm để hệ thống phát hiện điện thoại bị chỉnh giờ — nếu lệch nhiều, lần chấm công sẽ được
        chuyển cho quản lý xác minh.
      </p>

      {/* --- outcome -------------------------------------------------------- */}
      {outcome === null ? null : <OutcomeCard outcome={outcome} />}

      {/* --- actions, bottom third ------------------------------------------ */}
      <div className="mt-6 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Button
            size="lg"
            variant="success"
            disabled={checkedIn && !onBreak}
            loading={submit.isPending && pendingAction === 'CHECK_IN'}
            onClick={() => run('CHECK_IN')}
          >
            Vào ca
          </Button>
          <Button
            size="lg"
            variant="danger"
            disabled={!checkedIn}
            loading={submit.isPending && pendingAction === 'CHECK_OUT'}
            onClick={() => run('CHECK_OUT')}
          >
            Tan ca
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button
            size="lg"
            variant="secondary"
            disabled={!checkedIn || onBreak}
            loading={submit.isPending && pendingAction === 'BREAK_START'}
            onClick={() => run('BREAK_START')}
          >
            Nghỉ giữa ca
          </Button>
          <Button
            size="lg"
            variant="secondary"
            disabled={!onBreak}
            loading={submit.isPending && pendingAction === 'BREAK_END'}
            onClick={() => run('BREAK_END')}
          >
            Quay lại
          </Button>
        </div>

        <Button
          size="lg"
          variant="secondary"
          fullWidth
          loading={away.isPending}
          disabled={!online}
          onClick={() => away.mutate()}
        >
          Rời vị trí có phép
        </Button>
        <p className="text-center text-xs text-slate-500">
          “Rời vị trí có phép” đổi trạng thái làm việc của bạn, không phải một lần chấm công — bạn vẫn đang
          trong ca.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function LocationCard({
  geo,
  onRetry,
}: {
  readonly geo: ReturnType<typeof useGeolocation>['state'];
  readonly onRetry: () => void;
}): JSX.Element {
  return (
    <Card className="mt-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-slate-900">Vị trí</p>
          {geo.status === 'locating' ? (
            <p className="text-sm text-slate-600">Đang lấy vị trí…</p>
          ) : geo.fix !== null ? (
            <p className="text-sm text-slate-600">
              Độ chính xác khoảng <strong>{formatDistance(geo.fix.accuracyM)}</strong>
            </p>
          ) : (
            <p className="text-sm text-slate-600">Chưa có vị trí.</p>
          )}
        </div>
        <Button variant="secondary" onClick={onRetry}>
          Lấy lại
        </Button>
      </div>

      {geo.message === null ? null : (
        <p className="mt-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">{geo.message}</p>
      )}

      {geo.fix !== null && geo.fix.accuracyM > 120 ? (
        <p className="mt-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
          Độ chính xác {formatDistance(geo.fix.accuracyM)} là quá thấp để xác minh bạn ở trong phạm vi cửa
          hàng. Ra chỗ thoáng hoặc bật Wi-Fi rồi lấy lại vị trí.
        </p>
      ) : null}

      <p className="mt-2 text-xs text-slate-500">
        Khoảng cách tới cửa hàng do máy chủ tính và sẽ hiển thị sau khi bạn chấm công.
      </p>
    </Card>
  );
}

function OutcomeCard({ outcome }: { readonly outcome: Outcome }): JSX.Element {
  if (outcome.kind === 'QUEUED') {
    return (
      <Card className="mt-3 border-amber-300 bg-amber-50">
        <p className="font-bold text-amber-900">
          Đã lưu “{ATTENDANCE_ACTION_LABEL[outcome.action]}” trên máy, chờ gửi
        </p>
        <p className="mt-1 text-sm text-amber-900">
          Bạn bấm lúc {formatTime(outcome.at)} theo giờ điện thoại. Khi có mạng, hệ thống sẽ gửi lên và{' '}
          <strong>ghi nhận theo giờ máy chủ lúc nhận được</strong>. Nếu chênh lệch đáng kể, hãy báo quản lý để
          đối chiếu.
        </p>
      </Card>
    );
  }

  const { response } = outcome;
  const flags = response.riskFlags;

  return (
    <Card className="mt-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-bold text-slate-900">{ATTENDANCE_ACTION_LABEL[response.actionType]}</p>
        <StatusBadge tone={verificationTone(response.verificationResult)}>
          {verificationLabel(response.verificationResult)}
        </StatusBadge>
      </div>

      <div className="mt-2 divide-y divide-slate-100 border-t border-slate-100">
        <KeyValue label="Giờ máy chủ ghi nhận" value={formatTime(response.serverTime)} />
        <KeyValue label="Ngày công" value={response.businessDate} />
        <KeyValue
          label="Khoảng cách tới chi nhánh"
          value={formatDistance(response.distanceFromBranchM)}
          hint={
            response.insideGeofence === null
              ? 'Không xác định được vị trí'
              : response.insideGeofence
                ? 'Trong phạm vi cho phép'
                : 'Ngoài phạm vi cho phép'
          }
        />
        <KeyValue label="Lệch giờ thiết bị" value={formatSkew(response.clockSkewMs)} />
      </div>

      {response.insideGeofence === false ? (
        <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-bold">Bạn đang ở ngoài phạm vi cho phép của chi nhánh</p>
          <p className="mt-1">
            Máy chủ đo được {formatDistance(response.distanceFromBranchM)} từ vị trí của bạn tới cửa hàng. Lần
            chấm công này vẫn được ghi lại, nhưng sẽ cần quản lý xác nhận trước khi tính công. Nếu bạn thực sự
            đang ở cửa hàng (ví dụ tín hiệu GPS trong nhà bị lệch), hãy gửi khiếu nại chấm công trong mục Cá
            nhân.
          </p>
        </div>
      ) : null}

      {flags.length === 0 ? null : (
        <div className="mt-3">
          <p className="text-sm font-bold text-slate-800">Hệ thống ghi nhận {flags.length} dấu hiệu cần lưu ý</p>
          <ul className="mt-2 space-y-2">
            {flags.map((flag) => (
              <RiskFlagRow key={flag} flag={flag} />
            ))}
          </ul>
        </div>
      )}

      {response.verificationResult === 'PENDING_REVIEW' ? (
        <p className="mt-3 rounded-xl bg-slate-100 p-3 text-sm text-slate-700">
          <strong>Bạn cần làm gì tiếp:</strong> không cần chấm công lại. Lần chấm công này đã được lưu và đang
          chờ quản lý chi nhánh xác minh. Nếu quá ca mà chưa được duyệt, hãy gửi khiếu nại chấm công kèm giải
          thích trong mục Cá nhân → Khiếu nại.
        </p>
      ) : null}
    </Card>
  );
}

function RiskFlagRow({ flag }: { readonly flag: AttendanceRiskFlag }): JSX.Element {
  const label = RISK_FLAG_LABEL[flag];
  const advice = RISK_FLAG_ADVICE[flag];
  return (
    <li className="rounded-xl border border-slate-200 p-3">
      <p className="text-sm font-semibold text-slate-800">{label ?? flag}</p>
      <p className="mt-0.5 text-sm text-slate-600">{advice ?? 'Liên hệ quản lý chi nhánh nếu cần hỗ trợ.'}</p>
    </li>
  );
}

/** Best-effort network description, stored by the API as `network_info`. */
function describeNetwork(): string | null {
  const nav = navigator as Navigator & { connection?: { effectiveType?: string; type?: string } };
  const connection = nav.connection;
  if (connection === undefined) return null;
  const parts = [connection.type, connection.effectiveType].filter((v): v is string => typeof v === 'string');
  return parts.length === 0 ? null : parts.join('/').slice(0, 200);
}
